import { ApiRoutes, QueryError } from '@/common/interfaces';
import { apiExecutor, buildMutation, buildQuery } from '@/common/services';

import {
  PageCriticalCss,
  PagesCriticalCssFormFields,
  PagesCriticalCssGenerationSummary,
  PagesCriticalCssSettings,
  PagesCriticalCssSettingsFormFields,
  PagesCriticalCssStyleFile,
  PagesQueryKeys
} from '@/modules/Pages';

const siteRoute = (route: ApiRoutes, siteId: string): string => route.replace(':siteId', siteId);

const pageRoute = (route: ApiRoutes, siteId: string, pageId: string): string =>
  siteRoute(route, siteId).replace(':pageId', pageId);

export const useGetPagesCriticalCssSettingsQuery = (siteId: string) =>
  buildQuery<PagesCriticalCssSettings, QueryError>([PagesQueryKeys.PagesCriticalCssSettings, siteId], async () => {
    const { data } = await apiExecutor.get<PagesCriticalCssSettings>(siteRoute(ApiRoutes.PagesCriticalCssSettings, siteId));
    return data;
  });

export const useSavePagesCriticalCssSettingsMutation = (siteId: string) =>
  buildMutation<PagesCriticalCssSettings, QueryError, PagesCriticalCssSettingsFormFields>(
    [PagesQueryKeys.PagesCriticalCssSettings, siteId],
    async (postData) => {
      const { data } = await apiExecutor.post<PagesCriticalCssSettings>(siteRoute(ApiRoutes.PagesCriticalCssSettings, siteId), postData);
      return data;
    },
  );

export const useGetPagesCriticalCssStyleFilesQuery = (siteId: string) =>
  buildQuery<PagesCriticalCssStyleFile[], QueryError>([PagesQueryKeys.PagesCriticalCssStyleFiles, siteId], async () => {
    const { data } = await apiExecutor.get<PagesCriticalCssStyleFile[]>(siteRoute(ApiRoutes.PagesCriticalCssStyleFiles, siteId));
    return data;
  });

export const useGetPagesCriticalCssGenerationSummaryQuery = (siteId: string) =>
  buildQuery<PagesCriticalCssGenerationSummary, QueryError>([PagesQueryKeys.PagesCriticalCssGenerationSummary, siteId], async () => {
    const { data } = await apiExecutor.get<PagesCriticalCssGenerationSummary>(siteRoute(ApiRoutes.PagesCriticalCssGenerationSummary, siteId));
    return data;
  });

// without pageId the whole site is (re)generated
export const useGeneratePagesCriticalCssMutation = (siteId: string) =>
  buildMutation<void, QueryError, { pageId?: string } | void>([PagesQueryKeys.PagesCriticalCssGenerate, siteId], async (postData) => {
    await apiExecutor.post<void>(siteRoute(ApiRoutes.PagesCriticalCssGenerate, siteId), postData ?? {});
  });

export const useGetPageCriticalCssQuery = (pageId: string, siteId: string) =>
  buildQuery<PageCriticalCss, QueryError>([PagesQueryKeys.GetPageCriticalCss, siteId, pageId], async () => {
    const { data } = await apiExecutor.get<PageCriticalCss>(pageRoute(ApiRoutes.PageCriticalCss, siteId, pageId));
    return data;
  });

export const useEditPageCriticalCssMutation = (pageId: string, siteId: string) =>
  buildMutation<PageCriticalCss, QueryError, PagesCriticalCssFormFields>([PagesQueryKeys.EditPageCriticalCss, siteId, pageId], async (postData) => {
    const { data } = await apiExecutor.patch<PageCriticalCss>(pageRoute(ApiRoutes.PageCriticalCss, siteId, pageId), postData);
    return data;
  });
